"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";

/**
 * Accordion Component
 * - Matches visual styles of existing FaqAccordion items
 * - Does NOT render a section header; caller handles titles/wrappers
 *
 * Props
 * - items: Array<{ id: string, title: string, html?: string, content?: ReactNode }>
 * - singleOpen: boolean (when true, opening one closes others)
 * - allowCollapse: boolean (when true, clicking an open item closes it)
 * - className: string
 */
export default function Accordion({
  items = [],
  singleOpen = false,
  allowCollapse = true,
  className,
  onItemToggle = (id, item) => {},
}) {
  const initialState = useMemo(() => ({}), []);
  const [openMap, setOpenMap] = useState(initialState);

  const onToggle = useCallback(
    (id, item) => {
      setOpenMap((prev) => {
        const isOpen = !!prev[id];

        // If currently open and collapsing is allowed
        if (isOpen && allowCollapse) {
          return { ...prev, [id]: false };
        }

        // If singleOpen → close all others and open this one
        if (singleOpen) {
          const next = {};
          next[id] = true;
          return next;
        }
        // Multiple open allowed → toggle this one on
        return { ...prev, [id]: true };
      });
      onItemToggle(id, item);
    },
    [singleOpen, allowCollapse],
  );

  return (
    <div className={clsx("flex flex-col gap-4 md:gap-8", className)}>
      <div>
        {Array.isArray(items) &&
          items.map((item, index) => {
            const id = item.id ?? String(index);
            const isOpen = !!openMap[id];
            return (
              <div
                key={id}
                className={clsx(
                  "flex w-full flex-col py-4 md:py-6",
                  index !== items.length - 1 && "border-border-weak border-b",
                )}
              >
                <button
                  onClick={() => onToggle(id, item)}
                  className="flex w-full cursor-pointer items-center justify-between gap-6"
                  aria-expanded={isOpen}
                  aria-controls={`accordion-${id}`}
                >
                  <h3 className="color-foreground text-left text-lg leading-6 font-semibold">
                    {item.title}
                  </h3>
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-down"]}
                    className={clsx("text-xl transition-transform", {
                      "rotate-180": isOpen,
                    })}
                  />
                </button>

                <div
                  className={clsx(
                    "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out",
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    {item.html ? (
                      <div
                        id={`accordion-${id}`}
                        className="color-foreground pt-4 text-left text-base leading-6 font-normal"
                        aria-hidden={!isOpen}
                        dangerouslySetInnerHTML={{ __html: item.html }}
                      />
                    ) : (
                      <div
                        id={`accordion-${id}`}
                        className="color-foreground pt-4 text-left text-base leading-6 font-normal"
                        aria-hidden={!isOpen}
                      >
                        {item.content}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
